// DOJO-87 / HIVE-323 Phase 1: canonical published legal text for /terms and /privacy.
// Source of truth: wiki templates 26576e47 (ToS) and 72e80a42 (Privacy Policy),
// drafted by lex@shizuha.com (Legal) 2026-08-18. Edit the wiki template first,
// then mirror the change here — the published pages render this module.

export const TERMS_META = {
  title: 'Terms of Service — Shizuha',
  description:
    'Shizuha Global Terms of Service: acceptable use, autonomous agent liability, AI output, fees, grievance officer, and governing law for all Shizuha services.',
}

export const PRIVACY_META = {
  title: 'Privacy Policy — Shizuha',
  description:
    'Shizuha Global Privacy Policy: what personal data we collect, how we use it, India data location, DPDP Act 2023 rights, Dojo privacy, and grievance contacts.',
}

// Block shapes: {p: string} paragraph · {ul: [string]} bullet list · {table: {head: [], rows: [[]]}}
export const TERMS_SECTIONS = [
  {
    id: 'definitions',
    heading: '1. Definitions',
    blocks: [
      {
        ul: [
          '"Shizuha" / "we" / "us" / "our" means Shizuha Global Pvt. Ltd, a company incorporated in India (CIN U47912RJ2025PTC099211), registered office at Plot [address], Rajasthan, India.',
          '"Platform" / "Services" means the Shizuha platform and all associated services, including Connect (messaging, chat, jobs, Dojo), Pulse (task/project management), Wiki, Drive, Notes, Cortex (AI inference), Hive (agent runtime), and the shizuha.com website.',
          '"User" / "you" means any individual or entity that accesses or uses the Services.',
          '"Customer Content" / "Content" means any data, text, prompts, inputs, outputs, documents, messages, code, or other material you submit to, upload to, or generate through the Services.',
          '"Agent(s)" means autonomous AI agents operating on the Platform on instructions provided by a User.',
          '"AI Output" means content generated by the Services, including by Agents or AI models (e.g., Cortex inference, Dojo feedback).',
          '"End User" means any party who accesses the Services under your account or uses your application built on the Services.',
        ],
      },
    ],
  },
  {
    id: 'acceptance',
    heading: '2. Acceptance and Eligibility',
    blocks: [
      { p: '2.1 By creating an account or using the Services, you agree to these Terms, our Privacy Policy (/privacy), and any applicable supplemental terms.' },
      { p: '2.2 You must be at least 13 years old, or the minimum age required in your jurisdiction, and, if under 18, have parental/guardian consent.' },
      { p: '2.3 If you use the Services on behalf of an entity, you represent you have authority to bind that entity.' },
      { p: '2.4 India-focus: The Services are operated from India. Where you are an Indian user, these Terms are governed by Indian law (see §14).' },
    ],
  },
  {
    id: 'accounts',
    heading: '3. Accounts and Security',
    blocks: [
      { p: '3.1 You must provide accurate registration information and keep it current.' },
      { p: '3.2 You are responsible for all activity under your account and for safeguarding your credentials. Do not share API keys or account credentials.' },
      { p: '3.3 You must notify us promptly of any unauthorized access.' },
      { p: '3.4 We may suspend or terminate accounts that breach these Terms or applicable law.' },
    ],
  },
  {
    id: 'acceptable-use',
    heading: '4. Acceptable Use',
    blocks: [
      { p: '4.1 You agree not to use the Services to:' },
      {
        ul: [
          '(a) violate any applicable law or regulation;',
          '(b) infringe any third party\'s intellectual property or other rights;',
          '(c) distribute illegal content, including child sexual abuse material, terrorist content, or hate speech;',
          '(d) engage in fraud, money laundering, or other financial crime;',
          '(e) interfere with, disrupt, or attempt to gain unauthorized access to the Services or other users\' accounts;',
          '(f) reverse engineer, decompile, or attempt to extract the source code or underlying models of the Services (except as permitted by law);',
          '(g) use the Services to develop models that compete with Shizuha\'s products or services;',
          '(h) scrape or extract data from the Services beyond permitted API rate limits;',
          '(i) use the Services for surveillance, weapons development, or other harmful purposes.',
        ],
      },
      { p: '4.2 Connect/chat: You agree not to send unsolicited bulk messages (spam), harass other users, or use Connect to distribute malware or unlawful content. You are responsible for the content you send and receive.' },
      { p: '4.3 Dojo: Dojo provides AI interview practice and skill preparation. Dojo feedback is coaching, not a hiring decision. You agree not to misrepresent Dojo AI feedback as an actual employer\'s assessment.' },
    ],
  },
  {
    id: 'agents',
    heading: '5. Autonomous Agents',
    blocks: [
      { p: '5.1 Customer is principal. Where you deploy Agents on the Platform, you are responsible for all instructions, prompts, configurations, and parameters provided to Agents, and for all outputs, actions, communications, and decisions made by Agents acting on those instructions.' },
      { p: '5.2 Platform is provider. Shizuha provides the agent runtime and infrastructure but does not control, direct, or supervise Agent decision-making. Shizuha makes no warranty regarding the correctness, completeness, or suitability of Agent outputs.' },
      { p: '5.3 Indemnity. You indemnify Shizuha against third-party claims, losses, damages, and expenses arising from or relating to your Agents\' actions.' },
    ],
  },
  {
    id: 'ai-output',
    heading: '6. AI Output and Content',
    blocks: [
      { p: '6.1 Ownership. As between you and Shizuha, you retain ownership of your Input and own the AI Output generated from your Input, to the extent permitted by law. Shizuha assigns to you all its right, title, and interest in such AI Output.' },
      { p: '6.2 AI disclosure. AI-generated content is labeled or disclosed as AI-generated where required. You must not represent AI Output as wholly human-generated.' },
      { p: '6.3 No reliance. AI Output may be inaccurate, incomplete, or unsuitable for your purpose. You are responsible for evaluating AI Output before relying on it, including human review where appropriate. Do not use AI Output as the sole basis for decisions with legal or material impact on any person (credit, employment, education, housing, insurance, legal, or medical decisions).' },
      { p: '6.4 Dojo data: Your Dojo coaching data (session transcripts, scores, progress) belongs to you by default. We do not expose your individual prep scores to recruiters or companies without your explicit consent.' },
    ],
  },
  {
    id: 'fees',
    heading: '7. Fees, Payment, and Billing',
    blocks: [
      { p: '7.1 Fees for paid services (e.g., Dojo Pro, Interview Packs, subscriptions) are as described at the point of purchase and are exclusive of applicable taxes.' },
      { p: '7.2 You are responsible for all applicable taxes (including GST where applicable).' },
      { p: '7.3 Fees are non-refundable except as required by law or as stated in the applicable offer.' },
      { p: '7.4 Late or failed payment may result in suspension of paid services.' },
      { p: '7.5 Dojo: Free tier and paid tiers (e.g., ₹299 Interview Sprint, ₹499 Pro Monthly) are as described in the product. Plan boundaries and upgrade paths are set out in the product.' },
    ],
  },
  {
    id: 'ip',
    heading: '8. Intellectual Property',
    blocks: [
      { p: '8.1 As between you and Shizuha, Shizuha and its licensors own all rights in the Services, including software, models, and platform technology. You obtain a limited, non-exclusive, non-transferable right to use the Services.' },
      { p: '8.2 You retain all rights in your Content. You grant Shizuha a limited license to host, process, and display your Content solely to provide the Services to you.' },
      { p: '8.3 Feedback you provide may be used by Shizuha without restriction or compensation.' },
    ],
  },
  {
    id: 'privacy-data',
    heading: '9. Privacy and Data',
    blocks: [
      { p: '9.1 Your use of the Services is governed by our Privacy Policy (/privacy), which explains what data we collect, how we use it, and your rights.' },
      { p: '9.2 We process personal data in accordance with the Digital Personal Data Protection Act, 2023 (India) and applicable law.' },
      { p: '9.3 Where a Data Processing Agreement (DPA) applies, its terms are incorporated by reference.' },
    ],
  },
  {
    id: 'third-party',
    heading: '10. Third-Party Services and Providers',
    blocks: [
      { p: '10.1 The Services may integrate with or route to third-party services or providers (e.g., AI model providers, payment gateways).' },
      { p: '10.2 Third-party services are subject to their own terms and privacy policies. Shizuha is not responsible for third-party services.' },
      { p: '10.3 Cortex marketplace/community routes: Where the Platform routes inference through third-party providers, such routes are identified in the model catalog. Shizuha does not control or guarantee third-party provider output. Your inference data may be processed by the third-party provider per its disclosed data-use policy.' },
    ],
  },
  {
    id: 'disclaimers',
    heading: '11. Disclaimers and Limitation of Liability',
    blocks: [
      { p: '11.1 DISCLAIMER. THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, SHIZUHA DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.' },
      { p: '11.2 No warranty on AI output. We do not warrant that AI Output is accurate, complete, or suitable for any purpose. Use of AI Output is at your sole risk.' },
      { p: '11.3 Limitation of liability. To the maximum extent permitted by law, Shizuha\'s aggregate liability for any claim arising from or relating to the Services will not exceed the greater of (a) the amounts you paid for the Services giving rise to the claim in the 12 months preceding the claim, or (b) ₹10,000. Shizuha will not be liable for indirect, incidental, special, consequential, or exemplary damages, including lost profits, goodwill, or data.' },
      { p: '11.4 Nothing in these Terms excludes liability that cannot be excluded under applicable law.' },
    ],
  },
  {
    id: 'indemnification',
    heading: '12. Indemnification',
    blocks: [
      { p: 'You agree to indemnify and hold harmless Shizuha, its affiliates, and personnel from and against any third-party claims, losses, liabilities, and expenses (including reasonable legal fees) arising from or relating to (a) your use of the Services, (b) your Content, (c) your Agents\' actions, or (d) your breach of these Terms.' },
    ],
  },
  {
    id: 'termination',
    heading: '13. Termination and Suspension',
    blocks: [
      { p: '13.1 You may stop using the Services at any time.' },
      { p: '13.2 We may suspend or terminate your access if you breach these Terms, violate applicable law, or if your use creates risk or harm to the Services or others.' },
      { p: '13.3 On termination, we will provide data export per our data export procedures where applicable, subject to statutory retention requirements.' },
    ],
  },
  {
    id: 'governing-law',
    heading: '14. Governing Law and Dispute Resolution',
    blocks: [
      { p: '14.1 These Terms are governed by the laws of India.' },
      { p: '14.2 Courts: Subject to §14.3, disputes are subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka, India.' },
      { p: '14.3 Arbitration: Disputes exceeding ₹5,00,000 shall be resolved by arbitration in Bengaluru in accordance with the Arbitration and Conciliation Act, 1996, by a sole arbitrator appointed by Shizuha. The arbitration award shall be final and binding.' },
    ],
  },
  {
    id: 'grievance-officer',
    heading: '15. Grievance Officer and Compliance',
    blocks: [
      { p: '15.1 Grievance Officer: Appointed under the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021. Contact: grievance@shizuha.com. We respond to grievances within the timelines prescribed by law.' },
      { p: '15.2 Nodal Officer: For law-enforcement coordination. Contact: nodal@shizuha.com.' },
      { p: '15.3 Monthly compliance report: Published as required by the IT Rules 2021.' },
    ],
  },
  {
    id: 'changes',
    heading: '16. Changes to These Terms',
    blocks: [
      { p: '16.1 We may update these Terms from time to time. Material changes will be notified via the Services or email at least 30 days before they take effect, where required.' },
      { p: '16.2 Continued use of the Services after changes take effect constitutes acceptance.' },
    ],
  },
  {
    id: 'general',
    heading: '17. General',
    blocks: [
      { p: '17.1 These Terms, together with the Privacy Policy and any signed agreement, constitute the entire agreement between you and Shizuha.' },
      { p: '17.2 If any provision is held invalid, the remaining provisions continue in effect.' },
      { p: '17.3 Our failure to enforce a provision is not a waiver.' },
      { p: '17.4 You may not assign these Terms without our consent; we may assign them in connection with a merger or acquisition.' },
    ],
  },
]
